import React, { useEffect, useState } from "react";
import { api, UGX } from "../lib/api";
import { PageHeader, KpiCard, SectionCard, EmptyState } from "../components/Common";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import { Banknote, ArrowUpRight, ArrowDownRight, Wallet, Download, Plus, Users, TrendingUp, TrendingDown, Minus, X } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#1976D2","#2E7D32","#F59E0B","#D32F2F","#64748B","#0EA5E9","#8B5CF6"];
const REV_CATS = ["Consultation","Laboratory","Pharmacy","Imaging","Admissions","Procedures","Theatre","Maternity","Insurance","Other"];
const EXP_CATS = ["Payroll","Medication purchases","Laboratory supplies","Medical equipment","Utilities","Rent","Maintenance","Technology","Transportation","Cleaning","Security","Admin","Taxes","Other"];

function NetIncomeAndKpis({ stats, expensesMtd, arCount, onOpen }) {
  const revenue = Number(stats.revenue_month) || 0;
  const expenditure = Number(stats.expenditure_month != null ? stats.expenditure_month : expensesMtd) || 0;
  const netIncome = revenue - expenditure;
  const marginPct = revenue > 0 ? (netIncome / revenue) * 100 : 0;
  const netLast = Number(stats.net_income_last_month);
  const hasComparison = Number.isFinite(netLast);
  const delta = hasComparison ? netIncome - netLast : 0;
  const deltaPct = hasComparison && netLast !== 0 ? (delta / Math.abs(netLast)) * 100 : null;
  const isProfit = netIncome >= 0;

  const bg = isProfit ? "bg-[#e6f4ea] border-[#c1e2c8]" : "bg-[#fdecec] border-[#f5c2c2]";
  const iconBg = isProfit ? "bg-[#2E7D32] text-white" : "bg-[#B71C1C] text-white";
  const valColor = isProfit ? "text-[#1B5E20]" : "text-[#B71C1C]";
  const Icon = isProfit ? TrendingUp : TrendingDown;
  const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const deltaColor = delta > 0 ? "text-[#1B5E20]" : delta < 0 ? "text-[#B71C1C]" : "text-slate-500";

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <KpiCard testId="kpi-revenue-today" onClick={() => onOpen("today")} label="Revenue today" value={UGX(stats.revenue_today)} icon={Banknote} tone="green" hint="Click for today's entries" />

      <button
        type="button"
        onClick={() => onOpen("net")}
        className={`kpi-card border text-left ${bg} lg:col-span-1 cursor-pointer hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40`}
        data-testid="kpi-net-income"
      >
        <div className="flex items-start justify-between">
          <div className="text-[12px] text-slate-600 font-semibold tracking-wide uppercase">Net income MTD</div>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg}`}><Icon className="w-4 h-4" /></div>
        </div>
        <div className={`mt-3 stat-number text-3xl ${valColor}`} data-testid="net-income-value">{isProfit ? "" : "-"}{UGX(Math.abs(netIncome))}</div>
        <div className="mt-1 flex items-center justify-between gap-2 flex-wrap text-[11px]">
          <span className={`font-semibold ${valColor}`} data-testid="margin-pct">Margin {marginPct.toFixed(1)}%</span>
          {hasComparison ? (
            <span className={`inline-flex items-center gap-0.5 font-medium ${deltaColor}`} data-testid="net-delta">
              <DeltaIcon className="w-3 h-3" />
              {delta === 0 ? "flat vs last month" : `${delta > 0 ? "+" : "-"}${UGX(Math.abs(delta))}${deltaPct != null ? ` (${delta>0?"+":""}${deltaPct.toFixed(1)}%)` : ""} vs last month`}
            </span>
          ) : (
            <span className="text-slate-500">No prior month yet</span>
          )}
        </div>
      </button>

      <KpiCard testId="kpi-revenue-mtd" onClick={() => onOpen("month")} label="Revenue MTD" value={UGX(revenue)} icon={ArrowUpRight} tone="blue" hint="Click for month's entries" />
      <KpiCard testId="kpi-expenditure-mtd" onClick={() => onOpen("expenses")} label="Expenditure MTD" value={UGX(expenditure)} icon={ArrowDownRight} tone="amber" hint="Click for expense list" />
      <KpiCard testId="kpi-outstanding-ar" onClick={() => onOpen("ar")} label="Outstanding AR" value={UGX(stats.outstanding)} icon={Wallet} tone="red" hint={`${arCount} patients · click to view`} />
    </div>
  );
}

function FinanceDrillModal({ scope, stats, expenses, entries, onClose }) {
  const [billingPayments, setBillingPayments] = useState([]);
  useEffect(() => {
    if (scope === "today" || scope === "month") {
      api.get("/payments").then(({ data }) => setBillingPayments(data || [])).catch(() => setBillingPayments([]));
    }
  }, [scope]);

  const now = new Date();
  const todayIso = now.toISOString().slice(0, 10);
  const monthPrefix = now.toISOString().slice(0, 7);
  const inScope = (iso, s) => {
    const d = (iso || "").slice(0, 10);
    if (s === "today") return d === todayIso;
    return d.startsWith(monthPrefix);
  };

  const revManualToday = entries.filter((e) => inScope(e.created_at || e.revenue_date, "today"))
    .map((e) => ({ id: e.id || e.description, ts: e.created_at, source: `Manual · ${e.category}`, description: e.description, method: e.department || "—", amount: e.amount }));
  const revBillingToday = billingPayments.filter((p) => inScope(p.created_at, "today"))
    .map((p) => ({ id: p.payment_id, ts: p.created_at, source: `Billing · ${p.invoice_no || ""}`, description: p.patient_name || "—", method: p.method, amount: p.amount }));
  const revManualMonth = entries.filter((e) => inScope(e.created_at || e.revenue_date, "month"))
    .map((e) => ({ id: e.id || e.description, ts: e.created_at, source: `Manual · ${e.category}`, description: e.description, method: e.department || "—", amount: e.amount }));
  const revBillingMonth = billingPayments.filter((p) => inScope(p.created_at, "month"))
    .map((p) => ({ id: p.payment_id, ts: p.created_at, source: `Billing · ${p.invoice_no || ""}`, description: p.patient_name || "—", method: p.method, amount: p.amount }));

  const revToday = [...revBillingToday, ...revManualToday].sort((a,b) => (b.ts||"").localeCompare(a.ts||""));
  const revMonth = [...revBillingMonth, ...revManualMonth].sort((a,b) => (b.ts||"").localeCompare(a.ts||""));

  const expMonth = expenses.filter((e) => inScope(e.created_at || e.expense_date, "month"))
    .sort((a,b) => (b.created_at||"").localeCompare(a.created_at||""));

  const revenueMtd = Number(stats.revenue_month) || 0;
  const expenditureMtd = Number(stats.expenditure_month != null ? stats.expenditure_month : expenses.reduce((s,e)=>s+e.amount,0)) || 0;
  const net = revenueMtd - expenditureMtd;
  const margin = revenueMtd > 0 ? (net / revenueMtd) * 100 : 0;

  const titles = {
    today:    "Revenue today · entries",
    month:    "Revenue this month · entries",
    expenses: "Expenditure this month · entries",
    net:      "Revenue vs. Expenditure · this month",
  };

  const renderRows = (rows, sumTotal) => (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0">
        <tr><th className="text-left px-4 py-2">Time</th><th className="text-left px-4 py-2">Source</th><th className="text-left px-4 py-2">Detail</th><th className="text-left px-4 py-2">Method</th><th className="text-right px-4 py-2">Amount</th></tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r) => (
          <tr key={r.id} data-testid={`drill-row-${r.id}`}>
            <td className="px-4 py-2 text-xs tabular">{r.ts ? new Date(r.ts).toLocaleString(undefined,{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : "—"}</td>
            <td className="px-4 py-2 text-xs">{r.source}</td>
            <td className="px-4 py-2">{r.description || "—"}</td>
            <td className="px-4 py-2 text-xs">{r.method || "—"}</td>
            <td className="px-4 py-2 text-right tabular font-semibold">{UGX(r.amount || 0)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot className="bg-white sticky bottom-0 border-t-2 border-slate-300">
        <tr><td colSpan={4} className="px-4 py-3 text-right text-xs uppercase font-semibold text-slate-500">Total</td><td className="px-4 py-3 text-right tabular font-bold text-lg" data-testid="drill-total">{UGX(sumTotal)}</td></tr>
      </tfoot>
    </table>
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl border border-slate-200 max-w-4xl w-full max-h-[85vh] flex flex-col shadow-xl" data-testid={`finance-drill-${scope}`}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold text-slate-900">{titles[scope]}</div>
          <button data-testid="drill-close" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {scope === "today" && (revToday.length === 0
            ? <EmptyState title="No revenue recorded today" />
            : renderRows(revToday, revToday.reduce((s,r)=>s+(r.amount||0),0)))}
          {scope === "month" && (revMonth.length === 0
            ? <EmptyState title="No revenue this month" />
            : renderRows(revMonth, revMonth.reduce((s,r)=>s+(r.amount||0),0)))}
          {scope === "expenses" && (expMonth.length === 0
            ? <EmptyState title="No expenses this month" description="Log rent, utilities, supplies, medication purchases via Add expense." />
            : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0">
                  <tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Category</th><th className="text-left px-4 py-2">Description</th><th className="text-left px-4 py-2">Vendor</th><th className="text-right px-4 py-2">Amount</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {expMonth.map((e) => (
                    <tr key={e.expense_id} data-testid={`drill-exp-${e.expense_id}`}>
                      <td className="px-4 py-2 text-xs tabular">{(e.created_at||"").slice(0,10)}</td>
                      <td className="px-4 py-2"><span className="chip chip-slate text-[10px]">{e.category || "Uncategorised"}</span></td>
                      <td className="px-4 py-2">{e.description}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{e.vendor || "—"}</td>
                      <td className="px-4 py-2 text-right tabular font-semibold">{UGX(e.amount || 0)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-white sticky bottom-0 border-t-2 border-slate-300">
                  <tr><td colSpan={4} className="px-4 py-3 text-right text-xs uppercase font-semibold text-slate-500">Total expenditure MTD</td><td className="px-4 py-3 text-right tabular font-bold text-lg" data-testid="drill-total">{UGX(expenditureMtd)}</td></tr>
                </tfoot>
              </table>
            ))}
          {scope === "net" && (
            <div className="p-6 space-y-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Mini revenue vs. expenditure statement · this month</div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-4">
                  <div className="text-[11px] uppercase font-semibold text-[#1B5E20]">Revenue MTD</div>
                  <div className="text-3xl font-bold tabular text-[#1B5E20] mt-1">{UGX(revenueMtd)}</div>
                </div>
                <div className="rounded-lg border border-amber-100 bg-amber-50/60 p-4">
                  <div className="text-[11px] uppercase font-semibold text-[#915907]">Expenditure MTD</div>
                  <div className="text-3xl font-bold tabular text-[#915907] mt-1">{UGX(expenditureMtd)}</div>
                </div>
              </div>
              <div className={`rounded-lg border p-4 ${net >= 0 ? "border-emerald-200 bg-emerald-100/40" : "border-red-200 bg-red-50/60"}`} data-testid="net-statement">
                <div className={`text-[11px] uppercase font-semibold ${net >= 0 ? "text-[#1B5E20]" : "text-[#B71C1C]"}`}>Net income · Revenue − Expenditure</div>
                <div className={`text-4xl font-bold tabular mt-1 ${net >= 0 ? "text-[#1B5E20]" : "text-[#B71C1C]"}`}>{net < 0 ? "-" : ""}{UGX(Math.abs(net))}</div>
                <div className={`text-sm mt-1 ${net >= 0 ? "text-[#1B5E20]" : "text-[#B71C1C]"}`}>Margin {margin.toFixed(1)}% of revenue</div>
              </div>
              <div className="text-[11px] text-slate-500">Reconciles: {UGX(revenueMtd)} − {UGX(expenditureMtd)} = {UGX(net)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



export default function Finance() {
  const [stats, setStats] = useState({});
  const [trend, setTrend] = useState([]);
  const [ar, setAr] = useState([]);
  const [methods, setMethods] = useState([]);
  const [entries, setEntries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [showExp, setShowExp] = useState(false);
  const [showRev, setShowRev] = useState(false);
  const [drill, setDrill] = useState(null); // "today" | "month" | "expenses" | "net" | null

  const load = async () => {
    const [s, t, a, m, e, r] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/dashboard/revenue-trend"),
      api.get("/finance/outstanding-ar"),
      api.get("/finance/payment-methods"),
      api.get("/expenses").catch(() => ({data:[]})),
      api.get("/revenue-entries").catch(() => ({data:[]})),
    ]);
    setStats(s.data); setTrend(t.data || []); setAr(a.data || []);
    setMethods(m.data || []); setExpenses(e.data || []); setEntries(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const doExport = async () => {
    try {
      const res = await api.get("/finance/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `finance-${new Date().toISOString().slice(0,7)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Export failed"); }
  };

  const expenditureTotal = expenses.reduce((a,e)=>a+e.amount,0);
  const expByCat = Object.values(expenses.reduce((acc, e) => {
    const c = e.category || "Uncategorised";
    acc[c] = acc[c] || {name: c, value: 0, count: 0};
    acc[c].value += Number(e.amount) || 0;
    acc[c].count += 1;
    return acc;
  }, {})).sort((a,b) => b.value - a.value);
  const revByDept = Object.values(entries.reduce((acc, e) => { acc[e.category] = acc[e.category] || {name: e.category, value: 0}; acc[e.category].value += e.amount; return acc; }, {}));

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader title="Finance" subtitle="Internal hospital finances: payroll, supplier & operating expenses, budgets, bank reconciliation, accounting entries, revenue-vs-expense statements & audit exports. Patient revenue flows in automatically from Billing." actions={
        <div className="flex gap-2 flex-wrap">
          <button data-testid="add-revenue" onClick={() => setShowRev(true)} className="px-4 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add revenue</button>
          <button data-testid="add-expense" onClick={() => setShowExp(true)} className="px-4 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add expense</button>
          <button data-testid="finance-export" onClick={doExport} className="px-4 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold flex items-center gap-1.5"><Download className="w-4 h-4" /> Export report</button>
        </div>
      } />

      <NetIncomeAndKpis stats={stats} expensesMtd={expenses.reduce((a,e)=>a+e.amount,0)} arCount={ar.length}
        onOpen={(k) => {
          if (k === "ar") { setTimeout(() => document.getElementById("ar-anchor")?.scrollIntoView({ behavior: "smooth" }), 50); }
          else setDrill(k);
        }}
      />

      {drill && <FinanceDrillModal scope={drill} stats={stats} expenses={expenses} entries={entries} onClose={() => setDrill(null)} />}

      <SectionCard title="Revenue trend · last 14 days">
        <div className="p-4 h-64">
          {trend.length === 0 ? <EmptyState title="No revenue yet" /> : (
            <ResponsiveContainer><LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v)=>`${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v)=>UGX(v)} />
              <Line type="monotone" dataKey="revenue" stroke="#1976D2" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart></ResponsiveContainer>
          )}
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Payment method breakdown (MTD)">
          <div className="p-4 h-64">
            {methods.length === 0 ? <EmptyState title="No payments this month" /> : (
              <ResponsiveContainer><PieChart>
                <Tooltip formatter={(v)=>UGX(v)} /><Legend />
                <Pie data={methods.map(m=>({name:m.method,value:m.total}))} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {methods.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                </Pie>
              </PieChart></ResponsiveContainer>
            )}
          </div>
        </SectionCard>
        <SectionCard title="Expenditure by category" testId="expenditure-breakdown" actions={<span className="text-xs text-slate-500 tabular">MTD total: <span className="font-semibold text-slate-800">{UGX(expenditureTotal)}</span></span>}>
          <div className="p-4">
            {expByCat.length === 0 ? (
              <EmptyState title="No expenses recorded" description="Click 'Add expense' to log rent, utilities, supplies, medication purchases, equipment, and more." />
            ) : (
              <>
                {expByCat.length === 1 && (
                  <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-[#915907]" data-testid="single-category-hint">
                    Only one category recorded so far ({expByCat[0].name}). Log rent, utilities, supplies, equipment etc. via <span className="font-semibold">Add expense</span> to see a real breakdown.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                  <div className="h-56">
                    <ResponsiveContainer><PieChart>
                      <Tooltip formatter={(v, n) => [UGX(v), n]} />
                      <Pie data={expByCat} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                        {expByCat.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                      </Pie>
                    </PieChart></ResponsiveContainer>
                  </div>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-2" data-testid="exp-legend">
                    {expByCat.map((c, i) => {
                      const pct = expenditureTotal > 0 ? (c.value / expenditureTotal) * 100 : 0;
                      return (
                        <div key={c.name} className="flex items-center gap-2 text-xs" data-testid={`exp-cat-${c.name.replace(/\s+/g,"-")}`}>
                          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: COLORS[i%COLORS.length] }} />
                          <span className="flex-1 truncate text-slate-700">{c.name} <span className="text-slate-400">({c.count})</span></span>
                          <span className="tabular font-semibold">{UGX(c.value)}</span>
                          <span className="tabular text-slate-500 w-12 text-right">{pct.toFixed(1)}%</span>
                        </div>
                      );
                    })}
                    <div className="pt-2 mt-1 border-t border-slate-200 flex items-center gap-2 text-xs font-semibold">
                      <span className="flex-1 text-slate-700">Total</span>
                      <span className="tabular" data-testid="exp-total">{UGX(expenditureTotal)}</span>
                      <span className="w-12 text-right text-slate-500">100.0%</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </SectionCard>
      </div>

      <SectionCard id="ar-anchor" title="Outstanding accounts receivable" actions={<span className="text-xs text-slate-500">{ar.length} patients</span>}>
        {ar.length === 0 ? <EmptyState icon={Users} title="No outstanding balances" /> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-4 py-2.5">Patient</th><th className="text-left px-4 py-2.5">Patient ID</th><th className="text-right px-4 py-2.5">Invoices</th><th className="text-right px-4 py-2.5">Outstanding</th><th className="text-left px-4 py-2.5">Since</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ar.map(r => (
                <tr key={r.patient_id} data-testid={`ar-row-${r.patient_id}`}>
                  <td className="px-4 py-2.5">{r.patient_name}</td>
                  <td className="px-4 py-2.5 tabular">{r.patient_ths_id}</td>
                  <td className="px-4 py-2.5 text-right tabular">{r.invoices}</td>
                  <td className="px-4 py-2.5 text-right tabular font-semibold text-[#B71C1C]">{UGX(r.outstanding)}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{r.oldest?.slice(0,10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      {showExp && <EntryModal title="Add expense" categories={EXP_CATS} onClose={()=>setShowExp(false)} onSave={async (v)=>{ await api.post("/expenses", v); toast.success("Expense saved"); setShowExp(false); load(); }} kind="expense" />}
      {showRev && <EntryModal title="Add revenue entry" categories={REV_CATS} onClose={()=>setShowRev(false)} onSave={async (v)=>{ await api.post("/revenue-entries", v); toast.success("Revenue saved"); setShowRev(false); load(); }} kind="revenue" />}
    </div>
  );
}

function EntryModal({ title, categories, onClose, onSave, kind }) {
  const [form, setForm] = useState({ category: "", description: "", amount: 0, vendor: "", department: "General" });
  const errors = {
    category: !form.category,
    description: !form.description.trim(),
    amount: !form.amount || form.amount <= 0,
  };
  const invalid = errors.category || errors.description || errors.amount;
  const submit = () => { if (invalid) { toast.error("Category, description and amount are required"); return; } onSave(form); };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6" onClick={e=>e.stopPropagation()} data-testid={`${kind}-modal`}>
        <div className="font-semibold text-slate-900 mb-4">{title}</div>
        <div className="space-y-3">
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Category <span className="text-[#B71C1C]">*</span></span>
            <select data-testid={`${kind}-category`} value={form.category} onChange={e=>setForm({...form,category:e.target.value})} className={`mt-1 w-full h-10 px-3 rounded-lg border text-sm bg-white ${errors.category?"border-[#B71C1C]":"border-slate-200"}`}>
              <option value="">Select a category…</option>
              {categories.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            {kind === "expense" && <span className="text-[10px] text-slate-500 mt-0.5 block">Payroll, Rent, Utilities, Supplies, Equipment, Maintenance, etc.</span>}
          </label>
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Description <span className="text-[#B71C1C]">*</span></span>
            <input data-testid={`${kind}-desc`} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder={kind==="expense"?"e.g. August rent · Kampala clinic":"e.g. Antenatal package"} className={`mt-1 w-full h-10 px-3 rounded-lg border text-sm ${errors.description?"border-[#B71C1C]":"border-slate-200"}`} />
          </label>
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Amount (UGX) <span className="text-[#B71C1C]">*</span></span>
            <input data-testid={`${kind}-amount`} type="number" min="1" value={form.amount||""} onChange={e=>setForm({...form,amount:Number(e.target.value)})} className={`mt-1 w-full h-10 px-3 rounded-lg border text-sm tabular ${errors.amount?"border-[#B71C1C]":"border-slate-200"}`} />
          </label>
          {kind==="expense" && (<label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Vendor</span>
            <input data-testid={`${kind}-vendor`} value={form.vendor} onChange={e=>setForm({...form,vendor:e.target.value})} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>)}
          {kind==="revenue" && (<label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Department</span>
            <input value={form.department} onChange={e=>setForm({...form,department:e.target.value})} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>)}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-10 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid={`${kind}-save`} onClick={submit} disabled={invalid} className={`px-5 h-10 rounded-lg text-white text-sm font-semibold ${invalid?"bg-slate-300 cursor-not-allowed":"bg-[#2E7D32] hover:bg-[#1B5E20]"}`}>Save</button>
        </div>
      </div>
    </div>
  );
}
